import { useEffect, useState } from 'react';
import { FaBell, FaBellSlash } from 'react-icons/fa';
import {
  fetchNotificationPreferences,
  sendTestPushNotification,
  updateNotificationPreferences,
} from '../services/notificationPreferencesAPI';
import { registerPushNotifications } from '../services/pushNotificationService';
import './NotificationSettingsSection.css';

const PREFERENCE_ITEMS = [
  { key: 'booking', label: 'Booking notifications', hint: 'Confirmations, changes, and cancellations' },
  { key: 'reminders', label: 'Reminder notifications', hint: 'Check-in, meeting, and check-out reminders' },
  { key: 'payment', label: 'Payment notifications', hint: 'Payment success, refunds, and failures' },
  { key: 'host', label: 'Host notifications', hint: 'New bookings, guest inquiries, and refund requests' },
  { key: 'corporate', label: 'Corporate approval notifications', hint: 'Approval requests and policy alerts' },
  { key: 'marketing', label: 'Marketing notifications', hint: 'Promotions and announcements' },
];

const NotificationSettingsSection = () => {
  const [preferences, setPreferences] = useState(null);
  const [subscriptionsCount, setSubscriptionsCount] = useState(0);
  const [pushConfigured, setPushConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const browserPermission = typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchNotificationPreferences();
      setPreferences(data.preferences || {});
      setSubscriptionsCount(data.subscriptionsCount || 0);
      setPushConfigured(Boolean(data.pushConfigured));
    } catch (err) {
      setError(err.message || 'Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleToggle = async (key) => {
    if (!preferences) return;
    const next = { ...preferences, [key]: !preferences[key] };
    setPreferences(next);
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const data = await updateNotificationPreferences({ [key]: next[key] });
      setPreferences(data.preferences);
      setMessage('Notification preferences saved');
    } catch (err) {
      setError(err.message || 'Failed to save preferences');
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleEnablePush = async () => {
    setMessage('');
    setError('');
    const result = await registerPushNotifications({ requestPermission: true });
    if (result.success) {
      setMessage('Browser notifications enabled');
      load();
      return;
    }
    if (result.reason === 'awaiting_user_interaction') {
      setMessage('Click anywhere on the page to allow browser notifications');
      return;
    }
    if (result.reason === 'permission_denied') {
      setError('Notifications are blocked in your browser settings');
      return;
    }
    if (result.reason === 'disabled_by_env') {
      setError('Push notifications are disabled in this environment');
      return;
    }
    setError('Could not enable push notifications on this device');
  };

  const handleTestPush = async () => {
    setTesting(true);
    setMessage('');
    setError('');
    try {
      const data = await sendTestPushNotification();
      setMessage(data.message || 'Test notification sent');
    } catch (err) {
      setError(err.message || 'Failed to send test notification');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="notification-settings-loading">Loading notification settings...</div>;
  }

  return (
    <div className="notification-settings">
      <div className="notification-settings-status">
        <div>
          <strong>Browser permission:</strong> {browserPermission}
        </div>
        <div>
          <strong>Active devices:</strong> {subscriptionsCount}
        </div>
        <div>
          <strong>Server push:</strong> {pushConfigured ? 'Configured' : 'Not configured'}
        </div>
      </div>

      {browserPermission !== 'granted' && (
        <button type="button" className="notification-settings-enable" onClick={handleEnablePush}>
          <FaBell /> Enable browser notifications
        </button>
      )}

      {browserPermission === 'granted' && pushConfigured && subscriptionsCount > 0 && (
        <button type="button" className="notification-settings-enable" onClick={handleTestPush} disabled={testing}>
          <FaBell /> {testing ? 'Sending test...' : 'Send test notification'}
        </button>
      )}

      <div className="notification-settings-list">
        {PREFERENCE_ITEMS.map((item) => (
          <label key={item.key} className="notification-settings-item">
            <div>
              <span>{item.label}</span>
              <small>{item.hint}</small>
            </div>
            <input
              type="checkbox"
              checked={Boolean(preferences?.[item.key])}
              disabled={saving}
              onChange={() => handleToggle(item.key)}
            />
          </label>
        ))}
      </div>

      {browserPermission === 'denied' && (
        <p className="notification-settings-note">
          <FaBellSlash /> Notifications are blocked. Enable them in your browser site settings to receive alerts.
        </p>
      )}

      {message && <p className="notification-settings-message">{message}</p>}
      {error && <p className="notification-settings-error">{error}</p>}
    </div>
  );
};

export default NotificationSettingsSection;
